import { Component } from "react";
import autoBind from "react-autobind";
import queryString from "query-string";

import { RouterProps } from "@/components/shared/withRouting";
import { VendorUtilities } from "../../utilities/VendorUtilities";
import Modal from "react-modal";
import isEmpty from "lodash/isEmpty";

import Footer from "../shared/Footer";
import ModalHeader from "../modals/ModalHeader";
import ModalBody from "../modals/ModalBody";
import ReplicatedInfo from "./ReplicatedInfo";

import Alert from "../shared/Alert";
import { Button } from "../shared/Button";
import ExternalLink from "../shared/ExternalLink";
import { Icon } from "../shared/Icon";
import PasswordInput from "../shared/PasswordInput";
import TextInput from "../shared/TextInput";
import { BreakpointType, User } from "@/types";

interface LoginState {
  email: string;
  password: string;
  rememberMe: string;
  emailError: boolean;
  emailMessage: string;
  passwordError: boolean;
  passwordMessage: string;
  resetError: boolean;
  resetMessage: string;
  needsOtp: boolean;
  otpCode: string;
  resetPasswordEmailAddress: string;
  isTrialClosed: boolean;
}

interface LoginProps extends RouterProps {
  breakpoint: BreakpointType;
  clearServerError: () => void;
  dataLoading: {
    activationCodeLoading: boolean;
    googleLoginLoading: boolean;
    loginLoading: boolean;
    otpLoginLoading: boolean;
    passwordResetLoading: boolean;
    signupLoading: boolean;
  };
  displayModal: (type: string, isTrue: boolean) => void;
  err: boolean;
  errMessage: string;
  handleGoogleLogin: () => void;
  handleLogIn: (
    payload: { email: string; password: string },
    navigate: RouterProps["navigate"],
    parsedSearch: string | string[]
  ) => void;
  handleOtpLogin: (payload: { code: string }, callback: () => void) => void;
  hasRehydrated: boolean;
  hasToken: string[];
  loadingData: (type: string, loading: boolean) => void;
  logOutUser: () => void;
  modals: {
    forgotPasswordModal: boolean;
  };
  needsOtp: boolean;
  resetPasswordError: string;
  resetPasswordStep: string;
  sendPasswordResetEmail: (payload: { email: string }) => void;
  setResetPasswordStep: (step: string) => void;
  user: User;
}

class Login extends Component<LoginProps, LoginState> {
  refOtpCode: HTMLInputElement;
  constructor(props) {
    super(props);
    this.state = {
      email: "",
      password: "",
      rememberMe: "0",
      emailError: false,
      emailMessage: "",
      passwordError: false,
      passwordMessage: "",
      resetError: false,
      resetMessage: "",
      needsOtp: false,
      otpCode: "",
      resetPasswordEmailAddress: "",
      isTrialClosed: false,
    };
    autoBind(this);
  }

  clearErrors = () => {
    this.setState({
      emailError: false,
      emailMessage: "",
      passwordError: false,
      passwordMessage: "",
      resetError: false,
      resetMessage: "",
    });
  };

  handleFieldChange = (field, value) => {
    if (
      this.state.emailError ||
      this.state.passwordError ||
      this.state.resetError
    ) {
      this.clearErrors();
    }
    if (this.props.err) {
      this.props.clearServerError();
    }
    const nextState = {};
    nextState[field] = value;
    this.setState(nextState);
  };

  handleFormChange = (field, e) => {
    this.handleFieldChange(field, e.target.value);
  };

  buildPayload = () => {
    return {
      email: this.state.email.trim(),
      password: this.state.password.trim(),
    };
  };

  validateFields = () => {
    if (this.state.email.length === 0) {
      this.setState({
        emailError: true,
        emailMessage: "Email address is required",
      });
      return false;
    }
    if (this.state.password.length === 0) {
      this.setState({
        passwordError: true,
        passwordMessage: "Password is required",
      });
      return false;
    }
    return true;
  };

  handleGoogleLoginClick = async (ev) => {
    ev.preventDefault();
    this.props.handleGoogleLogin();
  };

  onSubmit = (e) => {
    e.preventDefault();
    this.clearErrors();
    const payload = this.buildPayload();
    const canSubmit = this.validateFields();
    const parsedSearch = queryString.parse(this.props.location.search);
    if (canSubmit) {
      this.props.handleLogIn(
        payload,
        this.props.navigate,
        parsedSearch.next
      );
    }
    window.localStorage.setItem("isAfterLogin", "true");
    this.setState({
      isTrialClosed: false,
    });
  };

  onOtpSubmit = (e) => {
    e.preventDefault();
    const parsedSearch = queryString.parse(this.props.location.search);
    const payload = {
      code: this.state.otpCode.trim(),
    };
    const callback = () => {
      this.props.navigate(`${parsedSearch.next || "/apps"}`, { replace: true });
    };
    this.props.handleOtpLogin(payload, callback);
  };

  resetPassword = () => {
    const payload = {
      email: this.state.resetPasswordEmailAddress,
    };
    if (!VendorUtilities.isEmailValid(this.state.resetPasswordEmailAddress)) {
      this.setState({
        resetError: true,
        resetMessage: "Please use a valid email address",
      });
      return false;
    }
    this.props.sendPasswordResetEmail(payload);
  };

  closeResetPassModal = () => {
    this.setState({
      resetPasswordEmailAddress: "",
      resetError: false,
      resetMessage: "",
    });
    this.props.displayModal("forgotPassword", false);
    this.props.setResetPasswordStep("info");
    this.props.clearServerError();
  };

  closeTrialExpiredModal = () => {
    this.setState({
      isTrialClosed: true,
    });
    this.props.displayModal("trialExpired", false);
  };

  componentDidUpdate(lastProps) {
    if (this.props.needsOtp !== lastProps.needsOtp && this.props.needsOtp) {
      this.setState({
        email: "",
        password: "",
      });
      if (this.refOtpCode) {
        setTimeout(() => {
          this.refOtpCode.focus();
        }, 50);
      }
    }
  }

  componentWillMount() {
     if (
      this.props.hasToken &&
      !!this.props.hasToken.length &&
      !this.props.needsOtp &&
      !isEmpty(this.props.user)
    ) {

      const parsedSearch = queryString.parse(this.props.location.search);
      const next = parsedSearch.next || "apps";
     // TODO: this isn't redirecting
      this.props.navigate(`/${next}`, { replace: true });
    }
  }

  componentDidMount() {
    VendorUtilities.purgeVOneData();
    this.props.clearServerError();
    this.props.loadingData("login", false);
    this.props.loadingData("otpLogin", false);
    this.props.loadingData("googleLogin", false);
    const persist = !window.localStorage.getItem("persist_session") ? "0" : "1";
    this.setState({
      rememberMe: persist,
    });

  }

  componentWillUnmount() {
    this.props.clearServerError();
  }

  render() {
    const { dataLoading, needsOtp, modals } = this.props;

    if (
      this.props.hasToken &&
      !!this.props.hasToken.length &&
      !needsOtp &&
      !isEmpty(this.props.user)
    ) {
      return null;
    }

    return (
      <div className="h-full w-full flex flex-col overflow-y-auto">
        <div className="flex flex-col-reverse shrink-0 min-[800px]:flex-row flex-auto w-full">
          <ReplicatedInfo />
          <div className="bg-page-bg lg:ml-0 lg:text-left flex-1 min-h-0 flex flex-col items-center justify-center pb-3 auth-pages">
            <div className="w-full max-w-[400px] mx-auto max-[430px]:w-[90%] lg:mx-0">
              <p className="font-['Montserrat',sans-serif] font-bold text-indigo-500 text-[30px] !text-left max-lg:mt-[70px] leading-[1.4]">
                {needsOtp ? "Verification Code" : "Log in"}
              </p>
            </div>

            {needsOtp || dataLoading.otpLoginLoading ? (
              <div className="w-full max-w-[400px] mx-auto max-[430px]:w-[90%] lg:mx-0">
                <form
                  onSubmit={(e) => {
                    this.onOtpSubmit(e);
                  }}
                >
                  <div className="FormSection-wrapper">
                    {this.props.err ? (
                      <Alert variant="error" className="mb-1.5">
                        {this.props.errMessage}
                      </Alert>
                    ) : null}
                    <div className="mb-3">
                      <TextInput
                        name="otpCode"
                        type="text"
                        className="Input"
                        ref={(input) => (this.refOtpCode = input)}
                        placeholder="Code"
                        value={this.state.otpCode}
                        onChangeValue={value => this.handleFieldChange("otpCode", value)}
                      />
                    </div>
                  </div>
                  <div className="FormButton-wrapper flex">
                    <Button
                      kind="primary-purple"
                      type="submit"
                      size="large"
                      className="flex-none mt-2.5 mb-5"
                      disabled={
                        dataLoading.otpLoginLoading || this.state.otpCode === ""
                      }
                    >
                      {dataLoading.otpLoginLoading ? "Verifying" : "Verify"}
                    </Button>
                    <span className="ml-5 flex flex-col justify-center">
                      <p
                        className="text-hex-337ab7 text-sm font-medium hover:underline hover:cursor-pointer"
                        onClick={() => this.props.logOutUser()}
                      >
                        Log out
                      </p>
                    </span>
                  </div>
                </form>
                <p className="text-xs text-gray-410 mt-5 leading-[1.4]">
                  Use a supported 2FA mobile app to retrieve your verification
                  code, such as{" "}
                  <ExternalLink
                    href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en"
                    className="text-hex-337ab7 font-medium"
                  >
                    Google Authenticator
                  </ExternalLink>
                  .
                </p>
              </div>
            ) : (
              <div className="w-full max-w-[400px] mx-auto max-[430px]:w-[90%] lg:mx-0">
                <form
                  onSubmit={(e) => {
                    this.onSubmit(e);
                  }}
                >
                  <div className="FormSection-wrapper">
                    {this.state.emailError ? (
                      <Alert variant="error" className="mb-1.5">
                        {this.state.emailMessage}
                      </Alert>
                    ) : null}
                    {this.props.err ? (
                      <Alert variant="error" className="mb-1.5">
                        {this.props.errMessage}
                      </Alert>
                    ) : null}
                    <div className="mb-5">
                      <TextInput
                        type="text"
                        name="email"
                        className="Input"
                        placeholder="Email address"
                        autoFocus
                        value={this.state.email}
                        onChangeValue={value => this.handleFieldChange("email", value)}
                      />
                    </div>
                    {this.state.passwordError ? (
                      <Alert variant="error" className="mb-1.5">
                        {this.state.passwordMessage}
                      </Alert>
                    ) : null}
                    <div className="mb-3">
                      <PasswordInput
                        name="password"
                        className="Input"
                        placeholder="Password"
                        autoComplete="current-password"
                        value={this.state.password}
                        onChangeValue={(value) =>
                          this.handleFieldChange("password", value)
                        }
                      />
                    </div>
                    <div className="forgot-pass">
                      <p
                        className="text-xs font-medium text-hex-2e96c2 hover:underline hover:cursor-pointer inline-block"
                        onClick={() => {
                          this.props.displayModal("forgotPassword", true);
                        }}
                      >
                        Forgot password?
                      </p>
                    </div>
                  </div>
                  <div className="FormButton-wrapper flex">
                    <Button
                      kind="primary-purple"
                      size="large"
                      className="flex-none mt-2.5 mb-5 w-full !text-lg"
                      disabled={dataLoading.loginLoading}
                    >
                      {dataLoading.loginLoading ? "Logging in" : "Log in"}
                    </Button>
                  </div>
                  <div className="flex items-center justify-center gap-4">
                    <div className="h-px w-[100px] bg-gray-410" />
                    <span className="text-sm font-semibold leading-none text-gray-410">or</span>
                    <div className="h-px w-[100px] bg-gray-410" />
                  </div>
                  <div className="mt-5">
                    <a
                      href="#"
                      onClick={this.handleGoogleLoginClick}
                      className="py-[10px] px-[25px] w-full text-base font-semibold rounded-[7px] border-2 border-solid border-hex-4285f4 text-hex-4285f4 bg-white hover:bg-hex-f3f7ff !flex items-center justify-center mb-3"
                      style={{ width: 'auto' }}
                    >
                      <span className="icon clickable google-g mr-3" />{" "}
                      Log in with Google
                    </a>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        this.props.navigate("/login-saml");
                      }}
                      className="py-[10px] px-[25px] w-full text-base font-semibold rounded-[7px] border-2 border-solid border-hex-4285f4 text-hex-4285f4 bg-white hover:bg-hex-f3f7ff !flex items-center justify-center mb-3"
                      style={{ width: 'auto' }}
                    >
                      <Icon name="lockBold" size="small" color="#4285f4" className="mr-3" />
                      Log in with SAML
                    </a>
                  </div>
                </form>
                <div className="mt-[15px] text-sm">
                  <p className="text-gray-410 text-center">
                    Don't have an account?{" "}
                    <a
                      href="/signup"
                      className="text-hex-2e96c2 hover:underline hover:cursor-pointer font-medium"
                    >
                      Sign up
                    </a>
                  </p>
                </div>
              </div>
            )}
            <div className="absolute bottom-[10px] right-[10px] !text-gray-200 max-lg:mx-auto max-lg:pt-[10px] max-lg:static max-lg:w-full max-lg:text-center max-lg:min-h-[100px]">
              <Footer />
            </div>
          </div>
        </div>

        <Modal
          isOpen={
            !this.state.isTrialClosed &&
            this.props.errMessage === "Trial expired"
          }
          ariaHideApp={false}
          contentLabel="Trial Expired Modal"
          className="Modal DefaultSize"
          onRequestClose={this.closeTrialExpiredModal}
        >
          <ModalHeader
            title="Trial expired"
            requestClose={this.closeTrialExpiredModal}
          />
          <ModalBody>
            <>
              Your free trial has ended. Please contact us at{" "}
              <ExternalLink
                href="mailto:contact@replicated.com"
                className="text-hex-337ab7 font-medium hover:underline hover:cursor-pointer"
              >
                contact@replicated.com{" "}
              </ExternalLink>
              if you need an extension.
              <div className="mt-8">
                <Button
                  kind="secondary-gray"
                  className="mr-3 mt-3"
                  onClick={this.closeTrialExpiredModal}
                >
                  Cancel
                </Button>
              </div>
            </>
          </ModalBody>
        </Modal>

        <Modal
          isOpen={modals.forgotPasswordModal}
          contentLabel="Forgot Password Modal"
          ariaHideApp={false}
          className="Modal DefaultSize"
          onRequestClose={this.closeResetPassModal}
        >
          <ModalHeader
            title="Reset your password"
            requestClose={this.closeResetPassModal}
          />
          <ModalBody>
            {this.props.resetPasswordStep === "success" ? (
              <>
                <p className="text-base font-normal text-gray-800 leading-[1.4] text-center">
                  Check your inbox. If there is an account with the specified
                  address, an email will be sent with instructions for resetting
                  the password.
                </p>
                <div className="mt-5 text-center">
                  <Button
                    type="button"
                    kind="primary-purple"
                    onClick={this.closeResetPassModal}
                  >
                    Ok, got it!
                  </Button>
                </div>
              </>
            ) : (
              <>
                {this.props.resetPasswordError ? (
                  <Alert variant="error" className="mb-1.5">
                    {this.props.resetPasswordError}
                  </Alert>
                ) : null}
                <div className="mb-8">
                  <p className="FormLabel m-0">
                    What is your email address?
                  </p>
                  {this.state.resetError ? (
                    <Alert variant="error" className="mb-1.5">
                      {this.state.resetMessage}
                    </Alert>
                  ) : null}
                  <TextInput
                    name="resetPasswordEmailAddress"
                    type="text"
                    className="Input"
                    placeholder="you@example.com"
                    value={this.state.resetPasswordEmailAddress}
                    onChangeValue={value =>
                      this.handleFieldChange("resetPasswordEmailAddress", value)
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Button
                    type="button"
                    kind="secondary-purple"
                    className="mr-2"
                    onClick={this.closeResetPassModal}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    kind="primary-purple"
                    disabled={
                      this.state.resetPasswordEmailAddress === "" ||
                      dataLoading.passwordResetLoading
                    }
                    onClick={this.resetPassword}
                  >
                    {dataLoading.passwordResetLoading
                      ? "Sending"
                      : "Send email"}
                  </Button>
                </div>
              </>
            )}
          </ModalBody>
        </Modal>
      </div>
    );
  }
}

export default Login;
